import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { jsPDF } from "jspdf";
import { Document, Packer, Paragraph, TextRun, Footer, AlignmentType } from "docx";
import { saveAs } from "file-saver";

export default function PreviewPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { transcript, qna_result, paragraph_result, mcq_result, fileName, format, youtubeUrl } = location.state || {};
  const [topic, setTopic] = useState("");

  // Translation State
  const [translateLanguage, setTranslateLanguage] = useState("en");
  const [translating, setTranslating] = useState(false);
  const [currentQnaResult, setCurrentQnaResult] = useState(qna_result || []);
  const [currentParagraphResult, setCurrentParagraphResult] = useState(paragraph_result || "");
  const [currentMcqResult, setCurrentMcqResult] = useState(mcq_result || []);

  const API_BASE_URL = process.env.REACT_APP_API_URL || (window.location.hostname === "localhost" ? "http://localhost:5000" : "");

  const handleTranslate = async () => {
    if (!translateLanguage) return;
    setTranslating(true);

    try {
      let contentToTranslate;
      if (format === "qna") contentToTranslate = currentQnaResult;
      else if (format === "paragraph") contentToTranslate = currentParagraphResult;
      else if (format === "mcq") contentToTranslate = currentMcqResult;

      const response = await fetch(`${API_BASE_URL}/translate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          content: contentToTranslate,
          target_language: translateLanguage,
          format: format
        })
      });

      const text = await response.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch (e) {
        console.error("Failed to parse response:", text);
        throw new Error(`Server returned non-JSON response: ${text.substring(0, 100)}...`);
      }

      if (data.error) throw new Error(data.error);

      if (format === "qna") setCurrentQnaResult(data.result);
      else if (format === "paragraph") setCurrentParagraphResult(data.result);
      else if (format === "mcq") {
        setCurrentMcqResult(data.result);
        setSelectedAnswers({});
        setShowScore(false);
      }

      alert(`Content translated to ${translateLanguage} successfully!`);

    } catch (error) {
      console.error("Translation error:", error);
      alert(`Translation failed: ${error.message}`);
    } finally {
      setTranslating(false);
    }
  };

  // MCQ State
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [showScore, setShowScore] = useState(false);

  if (!transcript) {
    return (
      <div className="container" style={{ textAlign: "center", marginTop: "50px" }}>
        <div className="glass-container">
          <h2>No transcript available</h2>
          <button className="btn-primary" onClick={() => navigate("/")}>Go Back</button>
        </div>
      </div>
    );
  }

  // Handle MCQ Answer Selection
  const handleAnswerSelect = (questionId, option) => {
    setSelectedAnswers(prev => ({
      ...prev,
      [questionId]: option
    }));
  };

  // Calculate Score
  const calculateScore = () => {
    let score = 0;
    mcq_result.forEach(q => {
      if (selectedAnswers[q.id] === q.correctAnswer) {
        score += 1;
      }
    });
    return score;
  };

  // ---------------- PDF ----------------
  const handleDownloadPDF = () => {
    if (!topic) return alert("Please enter a subject/topic name");

    const doc = new jsPDF({ unit: "pt", format: "a4" });
    const margin = 40;
    const pageWidth = doc.internal.pageSize.width - margin * 2;
    let y = margin;

    doc.setFontSize(16);
    doc.text(`Topic: ${topic}`, margin, y);
    y += 30;

    const today = new Date().toLocaleDateString();
    doc.setFontSize(10);
    doc.text(`Generated on: ${today}`, margin, y);
    y += 20;

    if (youtubeUrl) {
      doc.setFontSize(10);
      doc.setTextColor(0, 0, 255);
      const linkText = `YouTube URL: ${youtubeUrl}`;
      const lines = doc.splitTextToSize(linkText, pageWidth);
      lines.forEach((line) => {
        if (y > doc.internal.pageSize.height - margin) {
          doc.addPage();
          y = margin;
        }
        doc.textWithLink(line, margin, y, { url: youtubeUrl });
        y += 14;
      });
      doc.setTextColor(0, 0, 0);
      y += 10;
    }

    doc.setFontSize(12);

    // Helper to add branding footer on current/new pages
    const pageHeight = doc.internal.pageSize.height;
    const addBrandingFooter = () => {
      doc.setFontSize(8);
      doc.setFont(undefined, 'italic');
      doc.setTextColor(0, 0, 139); // Dark Blue
      doc.text('Made with: nottsify.com', doc.internal.pageSize.width - margin, pageHeight - 15, { align: 'right' });
      doc.setTextColor(0, 0, 0);
      doc.setFont(undefined, 'normal');
      doc.setFontSize(12);
    };

    const addText = (text, isBold = false, textColor = [0, 0, 0]) => {
      doc.setFont(undefined, isBold ? 'bold' : 'normal');
      doc.setTextColor(...textColor);
      const lines = doc.splitTextToSize(text, pageWidth);
      lines.forEach((line) => {
        if (y > pageHeight - margin - 20) {
          addBrandingFooter();
          doc.addPage();
          y = margin;
        }
        doc.text(line, margin, y);
        y += 16;
      });
      doc.setTextColor(0, 0, 0);
    };

    if (format === "qna") {
      const processQnAData = () => {
        if (!qna_result || !Array.isArray(qna_result)) return [];
        return qna_result.map(item => {
          if (typeof item === 'string') return { question: item, answer: "No answer provided", marks: "N/A" };
          return {
            question: item.question || "No question provided",
            answer: item.answer || "No answer provided",
            marks: item.marks || "N/A"
          };
        });
      };
      const processedQnA = processQnAData();

      const marksGroups = {
        "2": processedQnA.filter(qa => qa.marks === "2" || qa.marks === "2 Marks"),
        "5": processedQnA.filter(qa => qa.marks === "5" || qa.marks === "5 Marks"),
        "10": processedQnA.filter(qa => qa.marks === "10" || qa.marks === "10 Marks"),
        "other": processedQnA.filter(qa => !["2", "5", "10", "2 Marks", "5 Marks", "10 Marks"].includes(qa.marks))
      };

      Object.entries(marksGroups).forEach(([markType, questions]) => {
        if (questions.length > 0) {
          addText(`${markType === "other" ? "General" : markType + " Marks"} Questions:`, true, [0, 0, 0]);
          y += 10;
          questions.forEach((qa, idx) => {
            // Questions in red
            addText(`${idx + 1}. ${qa.question}`, true, [180, 0, 0]);
            // Answers in black
            addText(`   Answer: ${qa.answer}`, false, [0, 0, 0]);
            y += 10;
          });
          y += 10;
        }
      });

    } else if (format === "paragraph") {
      addText("Formatted Paragraph:", true);
      y += 10;
      addText(paragraph_result || "No paragraph content available.");
    } else if (format === "mcq") {
      addText("Multiple Choice Questions:", true);
      y += 10;
      mcq_result.forEach((q, idx) => {
        addText(`${idx + 1}. ${q.question}`, true);
        q.options.forEach((opt, optIdx) => {
          addText(`   ${String.fromCharCode(65 + optIdx)}. ${opt}`);
        });
        addText(`   Correct Answer: ${q.correctAnswer}`);
        y += 10;
      });
    }

    // Add branding footer to last page
    addBrandingFooter();

    const fileNamePDF = `${topic.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileNamePDF);
  };

  // ---------------- WORD DOCX ----------------
  const handleDownloadDOCX = async () => {
    if (!topic) return alert("Please enter a subject/topic name");

    const today = new Date().toLocaleDateString();
    const children = [
      new Paragraph({ children: [new TextRun({ text: `Topic: ${topic}`, bold: true, size: 28 })] }),
      new Paragraph({ children: [new TextRun({ text: `Generated on: ${today}`, size: 20 })] }),
      new Paragraph({ text: "" })
    ];

    if (youtubeUrl) {
      children.push(new Paragraph({ children: [new TextRun({ text: `YouTube URL: ${youtubeUrl}`, color: "0000FF", underline: {} })] }));
    }

    if (format === "qna") {
      const processQnAData = () => {
        if (!qna_result || !Array.isArray(qna_result)) return [];
        return qna_result.map(item => {
          if (typeof item === 'string') return { question: item, answer: "No answer provided", marks: "N/A" };
          return {
            question: item.question || "No question provided",
            answer: item.answer || "No answer provided",
            marks: item.marks || "N/A"
          };
        });
      };
      const processedQnA = processQnAData();
      const marksGroups = {
        "2": processedQnA.filter(qa => qa.marks === "2" || qa.marks === "2 Marks"),
        "5": processedQnA.filter(qa => qa.marks === "5" || qa.marks === "5 Marks"),
        "10": processedQnA.filter(qa => qa.marks === "10" || qa.marks === "10 Marks"),
        "other": processedQnA.filter(qa => !["2", "5", "10", "2 Marks", "5 Marks", "10 Marks"].includes(qa.marks))
      };

      Object.entries(marksGroups).forEach(([markType, questions]) => {
        if (questions.length > 0) {
          children.push(new Paragraph({ children: [new TextRun({ text: `${markType === "other" ? "General" : markType + " Marks"} Questions:`, bold: true })] }));
          questions.forEach((qa, idx) => {
            children.push(new Paragraph({ children: [new TextRun({ text: `${idx + 1}. ${qa.question}`, bold: true })] }));
            children.push(new Paragraph({ children: [new TextRun({ text: `Answer: ${qa.answer}` })] }));
            children.push(new Paragraph({ text: "" }));
          });
        }
      });

    } else if (format === "paragraph") {
      children.push(new Paragraph({ children: [new TextRun({ text: "Formatted Paragraph:", bold: true })] }));
      children.push(new Paragraph({ children: [new TextRun({ text: paragraph_result || "No paragraph content available." })] }));
    } else if (format === "mcq") {
      children.push(new Paragraph({ children: [new TextRun({ text: "Multiple Choice Questions:", bold: true })] }));
      mcq_result.forEach((q, idx) => {
        children.push(new Paragraph({ children: [new TextRun({ text: `${idx + 1}. ${q.question}`, bold: true })] }));
        q.options.forEach((opt, optIdx) => {
          children.push(new Paragraph({ children: [new TextRun({ text: `   ${String.fromCharCode(65 + optIdx)}. ${opt}` })] }));
        });
        children.push(new Paragraph({ children: [new TextRun({ text: `   Correct Answer: ${q.correctAnswer}`, italics: true })] }));
        children.push(new Paragraph({ text: "" }));
      });
    }

    const doc = new Document({
      sections: [{
        properties: {},
        footers: {
          default: new Footer({
            children: [
              new Paragraph({
                alignment: AlignmentType.RIGHT,
                children: [
                  new TextRun({
                    text: "Made with: nottsify.com",
                    italics: true,
                    size: 16, // 8pt
                    color: "00008B",
                  }),
                ],
              }),
            ],
          }),
        },
        children
      }]
    });
    const blob = await Packer.toBlob(doc);
    saveAs(blob, `${topic.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.docx`);
  };

  return (
    <div className="container" style={{ maxWidth: "900px", margin: "40px auto", textAlign: "center" }}>
      <h1 style={{ textAlign: "center" }}>📄 Preview: {fileName}</h1>

      <div className="glass-card" style={{
        background: "rgba(255, 153, 102, 0.1)",
        border: "1px solid #ff9966",
        marginBottom: "20px",
        textAlign: "center",
        padding: "15px",
        borderRadius: "12px",
        color: "#ff9966",
        fontWeight: "500"
      }}>
        ⚠️ <strong>Important:</strong> Download the content for better alignment notes. This content is not stored on our server, so you must download it now to keep a copy.
      </div>

      {youtubeUrl && (
        <div className="glass-card" style={{ textAlign: "center" }}>
          <strong>YouTube URL:</strong>{" "}
          <a href={youtubeUrl} target="_blank" rel="noopener noreferrer" style={{ color: "#4facfe" }}>
            {youtubeUrl}
          </a>
        </div>
      )}

      {/* HIDDEN TRANSCRIPT (Data preserved but not shown) */}

      {/* CONTENT DISPLAY */}
      {format === "qna" && (
        <div className="glass-container">
          <h2 style={{ marginBottom: "20px", borderBottom: "1px solid rgba(255,255,255,0.1)", paddingBottom: "10px" }}>Q&A Formatted Result</h2>
          {(!qna_result || qna_result.length === 0) ? (
            <div>No Q&A generated.</div>
          ) : (
            qna_result.map((qa, idx) => (
              <div key={idx} className="glass-card" style={{ background: "rgba(255, 255, 255, 0.05)", textAlign: "left" }}>
                <div style={{ color: "#ff4d4d", fontWeight: "bold", fontSize: "1.05em", marginBottom: "8px" }}>
                  Q{idx + 1}: {qa.question}
                </div>
                {qa.marks && <div style={{ marginBottom: "8px", fontSize: "0.78em", color: "#a29bfe", fontWeight: "600", textTransform: "uppercase", letterSpacing: "0.5px" }}>📝 {qa.marks} Marks</div>}
                <div style={{ color: "#e0e0e0", lineHeight: "1.7", borderLeft: "3px solid rgba(0,206,201,0.4)", paddingLeft: "12px", marginTop: "4px" }}>
                  <strong style={{ color: "#00cec9" }}>A:</strong> {qa.answer}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {format === "paragraph" && (
        <>
          <h2 style={{ marginTop: "30px" }}>Formatted Paragraph</h2>
          <div className="glass-container" style={{ lineHeight: "1.6", textAlign: "left" }}>
            {paragraph_result || "No paragraph content available."}
          </div>
        </>
      )}

      {format === "mcq" && (
        <div className="glass-container">
          <h2 style={{ marginBottom: "20px", borderBottom: "1px solid rgba(255,255,255,0.1)", paddingBottom: "10px" }}>Multiple Choice Questions</h2>
          {!mcq_result || mcq_result.length === 0 ? (
            <div>No MCQs generated.</div>
          ) : (
            <div>
              {showScore && (
                <div className="glass-card" style={{ textAlign: "center", marginBottom: "20px", background: "rgba(0, 255, 0, 0.1)", border: "1px solid #00ff00" }}>
                  <h3 style={{ margin: 0 }}>Your Score: {calculateScore()} / {mcq_result.length}</h3>
                </div>
              )}

              {mcq_result.map((q, idx) => (
                <div key={idx} className="glass-card" style={{ background: "rgba(255, 255, 255, 0.05)", textAlign: "left" }}>
                  <p style={{ fontWeight: "bold", fontSize: "1.1em" }}>{idx + 1}. {q.question}</p>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px", marginTop: "10px" }}>
                    {q.options.map((option, optIdx) => {
                      let style = { padding: "10px", borderRadius: "8px", border: "1px solid rgba(255,255,255,0.2)", cursor: "pointer", background: "rgba(255,255,255,0.05)" };

                      if (showScore) {
                        if (option === q.correctAnswer) {
                          style.background = "rgba(40, 167, 69, 0.5)"; // Green for correct
                          style.border = "1px solid #28a745";
                        } else if (selectedAnswers[q.id] === option && option !== q.correctAnswer) {
                          style.background = "rgba(220, 53, 69, 0.5)"; // Red for wrong
                          style.border = "1px solid #dc3545";
                        }
                      } else {
                        if (selectedAnswers[q.id] === option) {
                          style.background = "var(--secondary-color)";
                          style.color = "black";
                        }
                      }

                      return (
                        <div
                          key={optIdx}
                          onClick={() => !showScore && handleAnswerSelect(q.id, option)}
                          style={style}
                        >
                          {String.fromCharCode(65 + optIdx)}. {option}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}

              {!showScore && (
                <button className="btn-primary" onClick={() => setShowScore(true)} style={{ marginTop: "20px", width: "100%" }}>
                  Show Answers
                </button>
              )}
              {showScore && (
                <button className="btn-primary" onClick={() => { setShowScore(false); setSelectedAnswers({}); window.scrollTo(0, 0) }} style={{ marginTop: "20px", width: "100%" }}>
                  Retry Quiz
                </button>
              )}
            </div>
          )}
        </div>
      )}





      {/* DOWNLOAD SECTION */}
      <div className="glass-container" style={{ marginTop: "40px" }}>
        <h3>Download Content</h3>
        <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
          <input
            type="text"
            placeholder="Enter subject/topic name"
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            style={{ flex: 1 }}
          />
          <button className="btn-primary" onClick={handleDownloadPDF} style={{ background: "#ff9966" }}>
            Download PDF
          </button>
          <button className="btn-primary" onClick={handleDownloadDOCX} style={{ background: "#00c6ff" }}>
            Download Word
          </button>
        </div>
      </div>

      <button className="btn-primary" onClick={() => navigate("/")} style={{ marginTop: "20px", width: "100%", background: "transparent", border: "1px solid white" }}>
        Back to Home
      </button>

      <div style={{ height: "50px" }}></div>
    </div>
  );
}